import React, { useState, useEffect } from 'react';
import axios from "axios";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAppId } from "../context/AppIdContext";
import '../style/boothPramukh.css'
import Header from '../component/header'; // Added Header import

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BoothPramukhOfSaktikendra = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // --- NEW: Create Pramukh Modal State ---
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [createFormData, setCreateFormData] = useState({
        booth_no: '',
        name: '',
        mobile_no: ''
    });

    // 1. Pull language and userId from context
    const { appId, language, userId } = useAppId();
    const navigate = useNavigate();

    // --- Get passed state (user_id) from the previous page ---
    const location = useLocation();
    const passedUserId = location.state?.user_id || null;

    // 2. Set default language to English
    const lang = language || 'en';

    // 3. Translation Dictionary
    const t = {
        en: {
            loadingData: "Loading Data...",
            title: "Booth Pramukh and Booth List",
            subtitle: "Manage platform administrators and their permissions.",
            addBtn: "Add New Booth",
            searchPlaceholder: "Search by Booth Number...",
            boothNo: "Booth No.",
            totalPramukh: "Pramukh:",
            noPramukh: "No Pramukh Available",
            viewBtn: "View",
            createBtn: "Create Pramukh",
            noResultsPrefix: "No booth found matching",
            // Modal
            modalTitle: "Create Booth Pramukh",
            nameLabel: "Full Name",
            mobileLabel: "Mobile Number",
            cancelBtn: "Cancel",
            submitBtn: "Create",
            submitting: "Creating..."
        },
        hi: {
            loadingData: "डेटा लोड हो रहा है...",
            title: "बूथ प्रमुख और बूथ सूची",
            subtitle: "प्लेटफ़ॉर्म प्रशासकों और उनकी अनुमतियों का प्रबंधन करें।",
            addBtn: "नया बूथ जोड़ें",
            searchPlaceholder: "बूथ नंबर द्वारा खोजें...",
            boothNo: "बूथ नं.",
            totalPramukh: "प्रमुख:",
            noPramukh: "कोई प्रमुख उपलब्ध नहीं",
            viewBtn: "देखें",
            createBtn: "प्रमुख बनाएं",
            noResultsPrefix: "इससे मेल खाने वाला कोई बूथ नहीं मिला",
            // Modal
            modalTitle: "बूथ प्रमुख बनाएं",
            nameLabel: "पूरा नाम",
            mobileLabel: "मोबाइल नंबर",
            cancelBtn: "रद्द करें",
            submitBtn: "बनाएं",
            submitting: "बना रहा है..."
        },
        gu: {
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            title: "બૂથ પ્રમુખ અને બૂથ યાદી",
            subtitle: "પ્લેટફોર્મ સંચાલકો અને તેમની પરવાનગીઓનું સંચાલન કરો.",
            addBtn: "નવું બૂથ ઉમેરો",
            searchPlaceholder: "બૂથ નંબર દ્વારા શોધો...",
            boothNo: "બૂથ નં.",
            totalPramukh: "પ્રમુખ:",
            noPramukh: "કોઈ પ્રમુખ ઉપલબ્ધ નથી",
            viewBtn: "જુઓ",
            createBtn: "પ્રમુખ બનાવો",
            noResultsPrefix: "કોઈ બૂથ મળ્યું નથી",
            // Modal
            modalTitle: "બૂથ પ્રમુખ બનાવો",
            nameLabel: "પૂરું નામ",
            mobileLabel: "મોબાઇલ નંબર",
            cancelBtn: "રદ કરો",
            submitBtn: "બનાવો",
            submitting: "બનાવી રહ્યા છીએ..."
        }
    };

    const txt = t[lang];

    useEffect(() => {
        if (!appId || !passedUserId) {
            navigate(-1); // Go back if no valid data is provided
            return;
        }
        fetchUsers();
    }, [appId, passedUserId]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/display_booth_pramukh_by_sakti_pramuk`,
                {
                    app_id: appId,
                    user_id: passedUserId // Passing the user_id as requested
                }
            );

            if (response.data.success) {
                setUsers(response.data.data.result);
            } else {
                setUsers([]); // Clear users if success is false
            }
        } catch (err) {
            console.error("API Error:", err);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    // --- Handlers for Creating Pramukh ---
    const handleOpenCreateModal = (boothNo) => {
        setCreateFormData({
            booth_no: boothNo,
            name: '',
            mobile_no: ''
        });
        setIsCreateModalOpen(true);
    };

    const handleCreateInputChange = (e) => {
        const { name, value } = e.target;
        setCreateFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        if (!createFormData.name || !createFormData.mobile_no) {
            alert("Please fill in all fields.");
            return;
        }

        // Mobile Validation
        if (!/^\d{10}$/.test(createFormData.mobile_no)) {
            alert("Please enter a valid 10-digit mobile number.");
            return;
        }

        setIsSubmitting(true);

        const payload = {
            app_id: appId,
            name: createFormData.name,
            mobile_no: createFormData.mobile_no,
            user_type: "BP", // User Type for Booth Pramukh
            booth_no: createFormData.booth_no,
            booth_list: "",
            create_by: userId || "Admin"
        };

        try {
            const response = await axios.post(`${BASE_URL}/api/insert_user`, payload);
            if (response.data.success) {
                alert("Booth Pramukh created successfully!");
                setIsCreateModalOpen(false);
                fetchUsers(); // Refresh the list
            } else {
                alert("Error: " + JSON.stringify(response.data));
            }
        } catch (error) {
            console.error("API Error:", error);
            alert("Failed to create Booth Pramukh.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // 2. Filter Logic (Search by Booth Number)
    const filteredUsers = users.filter(user => {
        if (!searchTerm) return true;
        return user.booth_no?.toString().includes(searchTerm);
    });

    if (loading) {
        return (
            <div className="loader-wrapper">
                <div className="spinner"></div>
                <p className="loader-text">{txt.loadingData}</p>
                <style jsx>{`
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
                    .loader-text { color: #6B7280; font-weight: 500; font-size: 1.1rem; animation: pulse 1.5s ease-in-out infinite; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
                `}</style>
            </div>
        );
    }

    return (
        <>
            {/* --- NEW: Create Pramukh Modal --- */}
            {isCreateModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content-box" style={{ maxWidth: '400px' }}>
                        <div className="modal-header">
                            <div><h2>{txt.modalTitle}</h2></div>
                            <button className="close-btn-icon" onClick={() => setIsCreateModalOpen(false)}>
                                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <form onSubmit={handleCreateSubmit}>
                            <div className="modal-body-grid" style={{ display: 'flex', flexDirection: 'column', gap: '15px', padding: '20px 0' }}>
                                {/* Booth No - READ ONLY */}
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.boothNo}</label>
                                    <input type="text" value={createFormData.booth_no} disabled className="form-input" style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#f3f4f6', cursor: 'not-allowed' }} />
                                </div>
                                {/* Name Input */}
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.nameLabel}</label>
                                    <input type="text" name="name" value={createFormData.name} onChange={handleCreateInputChange} className="form-input" style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none' }} required />
                                </div>
                                {/* Mobile Input */}
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.mobileLabel}</label>
                                    <input type="text" name="mobile_no" value={createFormData.mobile_no} onChange={handleCreateInputChange} maxLength="10" className="form-input" style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none' }} required />
                                </div>
                            </div>
                            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button type="button" className="btn-cancel" onClick={() => setIsCreateModalOpen(false)} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}>{txt.cancelBtn}</button>
                                <button type="submit" className="btn-search-main" disabled={isSubmitting} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#2563eb', color: 'white', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                                    {isSubmitting ? txt.submitting : txt.submitBtn}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style jsx>{`
                .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.6); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
                .modal-content-box { background: white; border-radius: 12px; padding: 24px; width: 100%; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1); }
                .modal-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e5e7eb; padding-bottom: 15px; margin-bottom: 10px; }
                .close-btn-icon { background: none; border: none; cursor: pointer; color: #6b7280; }
                
                /* Professional Filter Bar Styling */
                .filter-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 16px;
                    margin-bottom: 24px;
                }

                .search-wrapper {
                    position: relative;
                    flex: 1;
                    max-width: 400px;
                }

                .search-icon {
                    position: absolute;
                    left: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    width: 20px;
                    height: 20px;
                }

                .search-input {
                    width: 100%;
                    padding: 12px 16px 12px 42px;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    font-size: 14px;
                    color: #1e293b;
                    outline: none;
                    transition: all 0.2s ease;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }

                .search-input:focus {
                    border-color: #2563eb;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
                }

                .btn-add-user {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    background-color: #2563eb;
                    color: white;
                    padding: 12px 20px;
                    border: none;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);
                    white-space: nowrap;
                }

                .btn-add-user:hover {
                    background-color: #1d4ed8;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 8px -1px rgba(37, 99, 235, 0.3);
                }

                /* User Grid - Desktop Default (4 columns) */
                .user-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 20px;
                }

                @media (max-width: 640px) {
                    /* Filter bar in one line on mobile */
                    .filter-container {
                        flex-direction: row;
                        align-items: center;
                        gap: 8px;
                    }

                    .search-wrapper {
                        flex: 1;
                        max-width: none;
                    }

                    .search-input {
                        padding: 10px 16px 10px 36px;
                        font-size: 13px;
                    }

                    .search-icon {
                        left: 10px;
                        width: 16px;
                        height: 16px;
                    }

                    .btn-add-user {
                        width: auto;
                        padding: 10px 12px;
                        font-size: 13px;
                        gap: 4px;
                        white-space: nowrap;
                    }

                    .btn-add-user svg {
                        width: 16px;
                        height: 16px;
                    }

                    /* Show at least 2 booths in mobile responsive */
                    .user-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 12px;
                    }

                    /* Adjust card padding for mobile */
                    .user-card {
                        padding: 16px 12px;
                    }

                    .booth-circle {
                        width: 60px;
                        height: 60px;
                        font-size: 20px;
                        margin: 8px auto;
                    }

                    .card-name {
                        font-size: 14px;
                        margin-top: 12px !important;
                    }

                    .btn-action {
                        padding: 8px 12px;
                        font-size: 13px;
                        margin-top: 12px;
                    }
                }
            `}</style>

            <div className={`app-container p-4 ${isCreateModalOpen ? 'blur-background' : ''}`}>

                {/* --- Header Component Integrated Here --- */}
                <Header title={txt.title} />

                {/* Professional Filter Bar with Search and Button in One Line */}
                <div className="filter-container">
                    <div className="search-wrapper">
                        <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder={txt.searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* User Grid */}
                <div className="user-grid">
                    {filteredUsers.length > 0 ? (
                        filteredUsers.map((user) => (
                            <div key={user.id} className="user-card">
                                <div className="menu-dots">
                                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 13a1 1 0 100-2 1 1 0 000 2zM12 7a1 1 0 100-2 1 1 0 000 2zM12 19a1 1 0 100-2 1 1 0 000 2z" />
                                    </svg>
                                </div>

                                <h5 style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                                    {txt.boothNo}
                                </h5>

                                <div className="booth-circle">
                                    {user.booth_no}
                                </div>

                                <h5 className="card-name text-primary" style={{ marginTop: '16px', fontSize: '16px' }}>
                                    {user.total_booth_pramukh ? (
                                        <>{txt.totalPramukh} <span style={{ color: 'var(--color-brand)' }}>{user.total_booth_pramukh}</span></>
                                    ) : (
                                        <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>{txt.noPramukh}</span>
                                    )}
                                </h5>

                                {user.total_booth_pramukh > 0 ? (
                                    <Link to='/booth_pramukh_detail_with_voters' state={{ selectedBoothNo: user.booth_no }} className='btn-action btn-green text-center text-decoration-none'>
                                        {txt.viewBtn}
                                    </Link>
                                ) : (
                                    <button
                                        className="btn-action btn-red"
                                        onClick={() => handleOpenCreateModal(user.booth_no)}
                                    >
                                        {txt.createBtn}
                                    </button>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="no-results" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "20px", color: "#6b7280" }}>
                            {txt.noResultsPrefix} "{searchTerm}"
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default BoothPramukhOfSaktikendra;